import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Search,
  Calculator,
} from 'lucide-react';

interface CodigoContable {
  id: string;
  codigo: string;
  nombre: string;
  activo: boolean;
}

export default function AdminCodigosContables() {
  const navigate = useNavigate();
  const { usuario: currentUser } = useAuth();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState<{ open: boolean; codigo: CodigoContable | null }>({ open: false, codigo: null });
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; codigo: CodigoContable | null }>({ open: false, codigo: null });

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  const [createForm, setCreateForm] = useState({ codigo: '', nombre: '' });
  const [editForm, setEditForm] = useState({ codigo: '', nombre: '', activo: true });

  const { data, isLoading } = useQuery({
    queryKey: ['codigos-contables'],
    queryFn: () => api.getCodigosContables(),
  });

  const codigos: CodigoContable[] = (data?.codigos as CodigoContable[]) || [];

  const filtered = search
    ? codigos.filter(c =>
        c.codigo.toLowerCase().includes(search.toLowerCase()) ||
        c.nombre.toLowerCase().includes(search.toLowerCase())
      )
    : codigos;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['codigos-contables'] });

  // --- Crear ---
  const handleCreate = async () => {
    setError('');
    if (!createForm.codigo || !createForm.nombre) {
      setError('Codigo y nombre son requeridos');
      return;
    }
    setIsProcessing(true);
    try {
      await api.createCodigoContable({
        codigo: createForm.codigo.trim(),
        nombre: createForm.nombre.trim(),
      });
      invalidate();
      setCreateModal(false);
      setCreateForm({ codigo: '', nombre: '' });
    } catch (err: any) {
      setError(err.message || 'Error al crear codigo contable');
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Editar ---
  const openEditModal = (c: CodigoContable) => {
    setEditForm({ codigo: c.codigo, nombre: c.nombre, activo: c.activo });
    setEditModal({ open: true, codigo: c });
    setError('');
  };

  const handleEdit = async () => {
    if (!editModal.codigo) return;
    setError('');
    if (!editForm.codigo || !editForm.nombre) {
      setError('Codigo y nombre son requeridos');
      return;
    }
    setIsProcessing(true);
    try {
      await api.updateCodigoContable(editModal.codigo.id, {
        codigo: editForm.codigo.trim(),
        nombre: editForm.nombre.trim(),
        activo: editForm.activo,
      });
      invalidate();
      setEditModal({ open: false, codigo: null });
    } catch (err: any) {
      setError(err.message || 'Error al actualizar codigo contable');
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Eliminar ---
  const handleDelete = async () => {
    if (!deleteModal.codigo) return;
    setIsProcessing(true);
    setError('');
    try {
      await api.deleteCodigoContable(deleteModal.codigo.id);
      invalidate();
      setDeleteModal({ open: false, codigo: null });
    } catch (err: any) {
      setError(err.message || 'Error al eliminar codigo contable');
    } finally {
      setIsProcessing(false);
    }
  };

  // --- Activar / desactivar ---
  const handleToggleActive = async (c: CodigoContable) => {
    try {
      await api.updateCodigoContable(c.id, { activo: !c.activo });
      invalidate();
    } catch (err: any) {
      console.error('Error al cambiar estado:', err);
    }
  };

  if (currentUser?.rol !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold">Acceso denegado</h2>
          <p className="text-muted-foreground mt-2">Solo los administradores pueden acceder a esta pagina.</p>
          <Button onClick={() => navigate('/')} className="mt-4">Volver al Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Calculator className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold">Codigos Contables</h1>
                  <p className="text-sm text-muted-foreground">Gestionar los codigos contables de los gastos</p>
                </div>
              </div>
            </div>
            <Button onClick={() => { setCreateModal(true); setError(''); }}>
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Codigo
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Buscador */}
        <div className="flex items-center gap-2 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por codigo o nombre..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="ml-auto text-sm text-muted-foreground">
            {filtered.length} codigo{filtered.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-lg shadow">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-muted-foreground">Cargando codigos contables...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center">
              <Calculator className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">
                {search ? `No se encontraron codigos para "${search}"` : 'No hay codigos contables registrados'}
              </p>
              {!search && (
                <Button className="mt-4" onClick={() => { setCreateModal(true); setError(''); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear primer codigo
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-40">Codigo</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="w-24">Estado</TableHead>
                  <TableHead className="text-right w-32">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => (
                  <TableRow key={c.id} className={!c.activo ? 'opacity-50' : ''}>
                    <TableCell className="font-mono font-medium text-blue-600">{c.codigo}</TableCell>
                    <TableCell className="font-medium">{c.nombre}</TableCell>
                    <TableCell>
                      <Badge
                        variant={c.activo ? 'success' : 'destructive'}
                        className="cursor-pointer"
                        onClick={() => handleToggleActive(c)}
                      >
                        {c.activo ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEditModal(c)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => { setDeleteModal({ open: true, codigo: c }); setError(''); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      {/* Modal Crear */}
      <Dialog open={createModal} onOpenChange={(open) => { if (!open) { setCreateModal(false); setError(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Nuevo Codigo Contable
            </DialogTitle>
            <DialogDescription>Agregue un nuevo codigo contable al sistema.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Codigo</label>
              <Input
                value={createForm.codigo}
                onChange={(e) => setCreateForm({ ...createForm, codigo: e.target.value })}
                placeholder="Ej: S519595"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Nombre</label>
              <Input
                value={createForm.nombre}
                onChange={(e) => setCreateForm({ ...createForm, nombre: e.target.value })}
                placeholder="Nombre de la cuenta contable"
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModal(false)} disabled={isProcessing}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={isProcessing}>
              {isProcessing ? 'Creando...' : 'Crear Codigo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Editar */}
      <Dialog open={editModal.open} onOpenChange={(open) => { if (!open) { setEditModal({ open: false, codigo: null }); setError(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Editar Codigo Contable
            </DialogTitle>
            <DialogDescription>Modifique los datos del codigo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium">Codigo</label>
              <Input
                value={editForm.codigo}
                onChange={(e) => setEditForm({ ...editForm, codigo: e.target.value })}
                placeholder="Codigo contable"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Nombre</label>
              <Input
                value={editForm.nombre}
                onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })}
                placeholder="Nombre de la cuenta contable"
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModal({ open: false, codigo: null })} disabled={isProcessing}>Cancelar</Button>
            <Button onClick={handleEdit} disabled={isProcessing}>
              {isProcessing ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Eliminar */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => { if (!open) setDeleteModal({ open: false, codigo: null }); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Eliminar Codigo Contable
            </DialogTitle>
            <DialogDescription>
              Esta seguro que desea eliminar este codigo? Si ya se asigno a alguna factura no se
              podra eliminar; en ese caso desactivalo. Esta accion no se puede deshacer.
              {deleteModal.codigo && (
                <div className="mt-4 p-3 bg-muted rounded-md">
                  <p className="font-medium">{deleteModal.codigo.codigo} - {deleteModal.codigo.nombre}</p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          {error && <p className="text-sm text-red-600 px-6">{error}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, codigo: null })} disabled={isProcessing}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isProcessing}>
              {isProcessing ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
